import { supabase } from "@/lib/supabase";
import type { 
  UserGameStats, 
  Achievement, 
  UserAchievement,
  LearningQueueItem,
  InsertLearningQueueItem 
} from "@shared/schema";

export const LEVEL_THRESHOLDS = [
  0, 100, 250, 500, 1000, 2000, 3500, 5500, 8000, 12000, 17000, 25000, 35000, 50000
];

export interface DailyGoals {
  sessionsTarget: number;
  sessionsCompleted: number;
  cardsTarget: number;
  cardsCompleted: number;
  lastResetDate: string | null;
  streakFreezeAvailable: boolean;
  streakFreezeUsedAt: string | null;
}

const DAILY_GOALS_KEY = "deeply_smart_daily_goals";

export const gamificationService = {
  getDailyGoals(userId: string): DailyGoals {
    const stored = localStorage.getItem(`${DAILY_GOALS_KEY}_${userId}`);
    const today = new Date().toISOString().split("T")[0];
    
    if (stored) {
      const goals = JSON.parse(stored) as DailyGoals;
      if (goals.lastResetDate === today) {
        return goals;
      }
    }
    
    const weekStart = new Date();
    const dayOfWeek = weekStart.getDay();
    const diff = weekStart.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    weekStart.setDate(diff);
    const weekStartStr = weekStart.toISOString().split("T")[0];
    
    const previousGoals = stored ? JSON.parse(stored) as DailyGoals : null;
    const freezeAvailable = !previousGoals?.streakFreezeUsedAt || 
      previousGoals.streakFreezeUsedAt < weekStartStr;
    
    const defaultGoals: DailyGoals = {
      sessionsTarget: 3,
      sessionsCompleted: 0,
      cardsTarget: 5,
      cardsCompleted: 0,
      lastResetDate: today,
      streakFreezeAvailable: freezeAvailable,
      streakFreezeUsedAt: previousGoals?.streakFreezeUsedAt || null,
    };
    
    this.saveDailyGoals(userId, defaultGoals);
    return defaultGoals;
  },

  saveDailyGoals(userId: string, goals: DailyGoals): void {
    localStorage.setItem(`${DAILY_GOALS_KEY}_${userId}`, JSON.stringify(goals));
  },

  incrementDailyGoal(userId: string, type: "sessions" | "cards", amount: number = 1): DailyGoals {
    const goals = this.getDailyGoals(userId);
    if (type === "sessions") {
      goals.sessionsCompleted = Math.min(goals.sessionsCompleted + amount, goals.sessionsTarget);
    } else {
      goals.cardsCompleted = Math.min(goals.cardsCompleted + amount, goals.cardsTarget);
    }
    this.saveDailyGoals(userId, goals);
    return goals;
  },

  useStreakFreeze(userId: string): boolean {
    const goals = this.getDailyGoals(userId);
    if (!goals.streakFreezeAvailable) {
      return false;
    }
    goals.streakFreezeAvailable = false;
    goals.streakFreezeUsedAt = new Date().toISOString().split("T")[0];
    this.saveDailyGoals(userId, goals);
    return true;
  },

  getDailyGoalProgress(userId: string): { percentage: number; completed: boolean } {
    const goals = this.getDailyGoals(userId);
    const sessionProgress = goals.sessionsCompleted / goals.sessionsTarget;
    const cardProgress = goals.cardsCompleted / goals.cardsTarget;
    const percentage = Math.round(((sessionProgress + cardProgress) / 2) * 100);
    return {
      percentage,
      completed: percentage >= 100,
    };
  },

  async getStats(userId: string): Promise<UserGameStats | null> {
    const { data, error } = await supabase
      .from("user_game_stats")
      .select("id, user_id, xp, level, current_streak, longest_streak, last_active_date, total_sessions, total_cards_reviewed, total_quizzes_taken, total_workouts_completed, created_at, updated_at")
      .eq("user_id", userId)
      .maybeSingle();

    if (error) {
      console.error("Error fetching game stats:", error);
    }
    return data;
  },

  async initializeStats(userId: string): Promise<UserGameStats | null> {
    const existing = await this.getStats(userId);
    if (existing) return existing;

    const { data, error } = await supabase
      .from("user_game_stats")
      .insert({
        user_id: userId,
        xp: "0",
        level: "1",
        current_streak: "0",
        longest_streak: "0",
        total_sessions: "0",
        total_cards_reviewed: "0",
        total_quizzes_taken: "0",
        total_workouts_completed: "0",
      })
      .select()
      .single();

    if (error) {
      console.error("Error initializing game stats:", error);
      return null;
    }
    return data;
  },

  async addXP(userId: string, amount: number, reason: string): Promise<{ newXP: number; levelUp: boolean; newLevel: number } | null> {
    const stats = await this.getStats(userId);
    if (!stats) {
      await this.initializeStats(userId);
      return this.addXP(userId, amount, reason);
    }

    const currentXP = parseInt(stats.xp, 10);
    const newXP = currentXP + amount;
    const currentLevel = parseInt(stats.level, 10);
    const newLevel = this.calculateLevel(newXP);
    const levelUp = newLevel > currentLevel;

    const { error } = await supabase
      .from("user_game_stats")
      .update({
        xp: newXP.toString(),
        level: newLevel.toString(),
        updated_at: new Date().toISOString(),
      })
      .eq("user_id", userId);

    if (error) {
      console.error("Error adding XP:", error);
      return null;
    }

    return { newXP, levelUp, newLevel };
  },

  calculateLevel(xp: number): number {
    for (let i = LEVEL_THRESHOLDS.length - 1; i >= 0; i--) {
      if (xp >= LEVEL_THRESHOLDS[i]) {
        return i + 1;
      }
    }
    return 1;
  },

  getXPForNextLevel(currentXP: number): { needed: number; progress: number } {
    const level = this.calculateLevel(currentXP);
    const currentThreshold = LEVEL_THRESHOLDS[level - 1] || 0;
    const nextThreshold = LEVEL_THRESHOLDS[level] || LEVEL_THRESHOLDS[LEVEL_THRESHOLDS.length - 1];
    
    const xpInLevel = currentXP - currentThreshold;
    const xpNeeded = nextThreshold - currentThreshold;
    
    return {
      needed: xpNeeded - xpInLevel,
      progress: Math.round((xpInLevel / xpNeeded) * 100),
    };
  },

  async updateStreak(userId: string): Promise<{ newStreak: number; isNewRecord: boolean } | null> {
    const stats = await this.getStats(userId);
    if (!stats) {
      await this.initializeStats(userId);
      return this.updateStreak(userId);
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const lastActive = stats.lastActiveDate ? new Date(stats.lastActiveDate) : null;
    lastActive?.setHours(0, 0, 0, 0);

    const currentStreak = parseInt(stats.currentStreak, 10);
    const longestStreak = parseInt(stats.longestStreak, 10);

    let newStreak = currentStreak;

    if (!lastActive) {
      newStreak = 1;
    } else {
      const diffDays = Math.floor((today.getTime() - lastActive.getTime()) / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) {
        return { newStreak: currentStreak, isNewRecord: false };
      } else if (diffDays === 1) {
        newStreak = currentStreak + 1;
      } else {
        newStreak = 1;
      }
    }

    const isNewRecord = newStreak > longestStreak;
    const newLongestStreak = Math.max(newStreak, longestStreak);

    const { error } = await supabase
      .from("user_game_stats")
      .update({
        current_streak: newStreak.toString(),
        longest_streak: newLongestStreak.toString(),
        last_active_date: today.toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq("user_id", userId);

    if (error) {
      console.error("Error updating streak:", error);
      return null;
    }

    return { newStreak, isNewRecord };
  },

  async incrementStat(userId: string, stat: "total_sessions" | "total_cards_reviewed" | "total_quizzes_taken" | "total_workouts_completed", amount: number = 1): Promise<void> {
    const stats = await this.getStats(userId);
    if (!stats) {
      await this.initializeStats(userId);
      return this.incrementStat(userId, stat, amount);
    }

    const currentValue = parseInt((stats as unknown as Record<string, string>)[stat] || "0", 10);
    
    const { error } = await supabase
      .from("user_game_stats")
      .update({
        [stat]: (currentValue + amount).toString(),
        updated_at: new Date().toISOString(),
      })
      .eq("user_id", userId);

    if (error) {
      console.error(`Error incrementing ${stat}:`, error);
    }
  },

  async getAchievements(): Promise<Achievement[]> {
    const { data, error } = await supabase
      .from("achievements")
      .select("id, name, description, icon, category, requirement, xp_reward, is_secret, created_at")
      .order("category", { ascending: true });

    if (error) {
      console.error("Error fetching achievements:", error);
      return [];
    }
    return data || [];
  },

  async getUserAchievements(userId: string): Promise<UserAchievement[]> {
    const { data, error } = await supabase
      .from("user_achievements")
      .select("id, user_id, achievement_id, unlocked_at, progress")
      .eq("user_id", userId);

    if (error) {
      console.error("Error fetching user achievements:", error);
      return [];
    }
    return data || [];
  },

  async unlockAchievement(userId: string, achievementId: string): Promise<UserAchievement | null> {
    const { data, error } = await supabase
      .from("user_achievements")
      .insert({
        user_id: userId,
        achievement_id: achievementId,
      })
      .select()
      .single();

    if (error) {
      console.error("Error unlocking achievement:", error);
      return null;
    }
    return data;
  },

  async getLearningQueue(userId: string): Promise<LearningQueueItem[]> {
    const { data, error } = await supabase
      .from("learning_queue")
      .select("id, user_id, title, description, source_id, space_id, topic_id, priority, status, due_date, completed_at, xp_reward, created_at, updated_at")
      .eq("user_id", userId)
      .neq("status", "completed")
      .order("priority", { ascending: false })
      .order("created_at", { ascending: true });

    if (error) {
      console.error("Error fetching learning queue:", error);
      return [];
    }
    
    return (data || []).map((item: any) => ({
      id: item.id,
      userId: item.user_id,
      title: item.title,
      description: item.description,
      sourceId: item.source_id,
      spaceId: item.space_id,
      topicId: item.topic_id,
      priority: item.priority,
      status: item.status,
      dueDate: item.due_date,
      completedAt: item.completed_at,
      xpReward: item.xp_reward,
      createdAt: item.created_at,
      updatedAt: item.updated_at,
    }));
  },

  async addToLearningQueue(item: InsertLearningQueueItem): Promise<LearningQueueItem | null> {
    const dbItem = {
      user_id: item.userId,
      title: item.title,
      description: item.description || null,
      source_id: item.sourceId || null,
      space_id: item.spaceId || null,
      topic_id: item.topicId || null,
      priority: item.priority || 'medium',
      status: item.status || 'pending',
      due_date: item.dueDate || null,
      xp_reward: item.xpReward || '5',
    };
    
    const { data, error } = await supabase
      .from("learning_queue")
      .insert(dbItem)
      .select()
      .single();

    if (error) {
      console.error("Error adding to learning queue:", error);
      return null;
    }
    
    if (!data) return null;
    
    return {
      id: data.id,
      userId: data.user_id,
      title: data.title,
      description: data.description,
      sourceId: data.source_id,
      spaceId: data.space_id,
      topicId: data.topic_id,
      priority: data.priority,
      status: data.status,
      dueDate: data.due_date,
      completedAt: data.completed_at,
      xpReward: data.xp_reward,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  },

  async updateQueueItem(id: string, updates: Partial<LearningQueueItem>): Promise<LearningQueueItem | null> {
    const { data, error } = await supabase
      .from("learning_queue")
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error("Error updating queue item:", error);
      return null;
    }
    return data;
  },

  async completeQueueItem(id: string, userId: string): Promise<{ xpEarned: number } | null> {
    const { data: item } = await supabase
      .from("learning_queue")
      .select("xp_reward")
      .eq("id", id)
      .single();

    if (!item) return null;

    const xpReward = parseInt(item.xp_reward || "5", 10);

    const { error } = await supabase
      .from("learning_queue")
      .update({
        status: "completed",
        completed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq("id", id);

    if (error) {
      console.error("Error completing queue item:", error);
      return null;
    }

    await this.addXP(userId, xpReward, "learning_queue_completed");
    
    return { xpEarned: xpReward };
  },

  async deleteQueueItem(id: string): Promise<boolean> {
    const { error } = await supabase
      .from("learning_queue")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("Error deleting queue item:", error);
      return false;
    }
    return true;
  },
};
